import React, { useMemo, useState } from "react";

import {
  calculateFootprintingProgress,
  isPercentageComplete,
  queryIsLoading,
  useGetFarms,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprints,
  useGetFootprintingFarmFootprint,
  useParams,
  useSubmitFootprintingCropPrimaryData,
  useSubmitFootprintingDairyPrimaryData,
  useSubmitFootprintingFarmPrimaryData,
  useTranslation,
} from "utils";

import { CropFootprintReturnBody, DairyFootprintReturnBody } from "models";

import { Button, Col, Flex, MainPage, Row } from "components";

import { FootprintingCropReview } from "pages/app/footprinting/components/FootprintingCropReview";
import { FootprintingDairyReview } from "pages/app/footprinting/components/FootprintingDairyReview";
import { FootprintingFarmReview } from "pages/app/footprinting/components/FootprintingFarmReview";
import { FootprintingReviewSteps } from "pages/app/footprinting/components/FootprintingReviewSteps";
import { FootprintingSuccessScreen } from "pages/app/footprinting/components/FootprintingSuccessScreen";
import { SubmitForVerificationCard } from "pages/app/footprinting/components/SubmitForVerificationCard";

interface ReviewProps {
  year: number;
}

export const Review = ({ year }: ReviewProps) => {
  const { t } = useTranslation(["footprinting"]);
  const { currentFarm, isLoading: isGetFarmsLoading } = useGetFarms();
  const carbonProgram = currentFarm?.carbonProgram;
  const [currentStep, setCurrentStep] = useState(0);
  const [allCheckboxesChecked, setAllCheckboxesChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const farmProgressQuery = useGetFootprintingFarmFootprint(currentFarm?.id, year);
  const cropProgressQueries = useGetFootprintingCropFootprints(currentFarm?.id, [year], carbonProgram?.farmLevelFootprinting?.programCrops ?? []);
  const dairyProgressQueries = useGetFootprintingDairyFootprints(currentFarm?.id, [year]);

  const isLoading = isSubmitting && (isGetFarmsLoading || [farmProgressQuery, ...cropProgressQueries].some((query) => queryIsLoading(query)));

  const { readyToSubmit, isSubmitted } = useMemo(() => {
    if (isLoading) {
      return calculateFootprintingProgress(undefined, []);
    }

    return calculateFootprintingProgress(farmProgressQuery.data, [
      ...cropProgressQueries.filter((cpq) => !!cpq.data).map((cpq) => cpq.data),
      ...dairyProgressQueries.filter((dpq) => !!dpq.data).map((dpq) => dpq.data),
    ]);
  }, [isLoading, farmProgressQuery.data, cropProgressQueries, dairyProgressQueries]);

  const cropsToReview = cropProgressQueries
    .filter((cpq) => cpq.data !== undefined && isPercentageComplete(cpq.data.completionPct))
    .map((cpq) => cpq.data as CropFootprintReturnBody);

  const dairiesToReview = dairyProgressQueries
    .filter((dpq) => dpq.data !== undefined && isPercentageComplete(dpq.data.completionPct))
    .map((dpq) => dpq.data as DairyFootprintReturnBody);

  const { mutate: mutateSubmitCropPrimaryData } = useSubmitFootprintingCropPrimaryData(
    currentFarm?.id ?? "",
    year,
    cropsToReview.map((crop) => ({ cropId: crop.cropId, primaryDataId: crop.footprint?.id ?? "" }))
  );

  const { mutate: mutateSubmitFarmPrimaryData } = useSubmitFootprintingFarmPrimaryData(
    currentFarm?.id ?? "",
    year,
    farmProgressQuery.data?.footprint?.id ?? ""
  );

  const { mutate: mutateSubmitDairyPrimaryData } = useSubmitFootprintingDairyPrimaryData(
    currentFarm?.id ?? "",
    year,
    farmProgressQuery.data?.footprint?.id ?? ""
  );

  const handleCheckboxesChange = (allChecked: boolean) => {
    setAllCheckboxesChecked(allChecked);
  };

  const handleSubmit = () => {
    if (!allCheckboxesChecked || isLoading || !currentFarm?.id || !readyToSubmit) {
      return;
    }

    setIsSubmitting(true);
    Promise.all([mutateSubmitFarmPrimaryData(), mutateSubmitCropPrimaryData(), mutateSubmitDairyPrimaryData()]).finally(() => setIsSubmitting(false));
  };

  // Just to get TS to stop complaining about farmId being maybe undefined
  if (!currentFarm?.id) {
    return null;
  }

  if (carbonProgram !== undefined && isSubmitted) {
    return <FootprintingSuccessScreen carbonProgram={carbonProgram ?? undefined} year={year} farmId={currentFarm?.id} />;
  }

  const steps = [
    <FootprintingFarmReview key={currentFarm?.id} year={year} />,
    ...cropsToReview.map((ctr) => <FootprintingCropReview key={ctr.cropId} cropId={ctr.cropId} year={year} />),
    ...(carbonProgram?.platformFeatures.footprinting?.dairy === true ? dairiesToReview : []).map((dtr) => (
      <FootprintingDairyReview key={dtr.footprint?.id} year={year} />
    )),
    <SubmitForVerificationCard
      key="submitPage"
      farmId={currentFarm?.id}
      carbonProgram={carbonProgram ?? undefined}
      year={year}
      onAllCheckboxesChecked={handleCheckboxesChange}
      formSubmitted={isSubmitted}
    />,
  ];

  return (
    <Row justify="space-between">
      <Col sm={10} xs={8}>
        <FootprintingReviewSteps
          farmId={currentFarm?.id}
          cropIds={cropsToReview.map((cfd) => cfd.cropId)}
          dairy={dairiesToReview.length > 0}
          currentStep={currentStep}
          year={year}
          isLoading={isLoading}
        />
      </Col>

      <Col sm={13} xs={10}>
        {steps[currentStep]}
        <Flex justify="space-between" className="padding-125 margin-bottom-100">
          {currentStep > 0 && (
            <Button
              dataTestId="footprinting-review-back-button"
              className="review-navigation-button"
              type="tertiary"
              onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
              disabled={isLoading}
            >
              {t("review.back")}
            </Button>
          )}
          {currentStep < steps.length - 1 ? (
            <Button
              dataTestId="footprinting-review-next-button"
              className="review-navigation-button"
              type="primary"
              onClick={() => setCurrentStep((prev) => Math.min(prev + 1, steps.length + 1))}
              disabled={isLoading}
            >
              {t("review.continue")}
            </Button>
          ) : (
            <Button
              dataTestId="footprinting-review-submit-button"
              className="review-navigation-button"
              type="primary"
              onClick={handleSubmit}
              disabled={!readyToSubmit || !allCheckboxesChecked || isLoading}
            >
              {t("review.submit", { year })}
            </Button>
          )}
        </Flex>
      </Col>
    </Row>
  );
};

export const Wrapper = (p: React.PropsWithChildren) => {
  const { t } = useTranslation([]);
  const params = useParams();
  const { currentFarm } = useGetFarms();

  const year = Number.parseInt(params.year ?? "");
  if (!year) {
    return null;
  }

  return (
    <MainPage
      pageName="Review & Submit"
      navigationCrumbs={{
        crumbs: [
          { name: currentFarm?.name || t("modules:navigation.overview.farm"), replacedByFarmSwitch: true },
          { name: t("footprinting:footprintingLandingPage.breadcrumb"), link: "footprinting" },
          { name: "Review & Submit" },
        ],
      }}
    >
      <Review {...p} year={year} />
    </MainPage>
  );
};

export default Wrapper;
